/**
 * LandlordDashboardV2 — new dashboard shell shipped by the design pass.
 *
 * Design intent (per /app/design_guidelines.json):
 *   Field Notes × Stripe × Notion. Sharp edges, IBM Plex, dense KPI grid,
 *   a "Next Best Action" strip above the fold, right-anchored slide-over
 *   for detail views (no stacked modals).
 *
 * Wiring:
 *   • Fetches /api/briefing/daily so the KPI cards reflect real state.
 *   • Uses `getPersonalizedGreeting()` + `getLegalCallout()` from
 *     personalizer.js so the greeting scales with role/portfolio_size.
 *   • Delegates "review draft" → real POST /api/landlord/announcements/draft
 *     (falls back to a template automatically when LLM budget is out —
 *     see server.py _smart_engine_soft_handler).
 *   • Every interactive element carries data-testid.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home, Users, Wrench, FileText, Bell, Search, AlertCircle,
  Building2, CheckCircle2, ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { getPersonalizedGreeting, getLegalCallout } from "@/lib/personalizer";
import NextBestAction from "@/components/NextBestAction";
import ReferralBanner from "@/components/dashboard/ReferralBanner";
import InlineSlideOver from "@/components/InlineSlideOver";
import { api } from "@/lib/api";

const NAV = [
  { icon: Home, label: "Overview", to: "/dashboard" },
  { icon: Building2, label: "Properties", to: "/properties" },
  { icon: Users, label: "Leases", to: "/leases" },
  { icon: Wrench, label: "Maintenance", to: "/tickets" },
  { icon: FileText, label: "Books", to: "/books" },
];

export default function LandlordDashboardV2() {
  const { user } = useAuth() || {};
  const t = useT();
  const [briefing, setBriefing] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    api.get("/briefing/daily").then((r) => setBriefing(r.data || {})).catch(() => setBriefing({}));
  }, []);

  const greeting = useMemo(() => getPersonalizedGreeting(user, t), [user, t]);
  const legal = useMemo(() => getLegalCallout(user, t), [user, t]);

  const kpis = useMemo(() => {
    const b = briefing || {};
    return [
      { label: t("v2.kpi_collected", "Rent collected"), val: b.rent_collected_display || "—" },
      { label: t("v2.kpi_occupancy", "Occupancy"), val: b.occupancy_display || "—" },
      { label: t("v2.kpi_open_tickets", "Open tickets"), val: String(b.open_tickets ?? "—") },
    ];
  }, [briefing, t]);

  const openDraftReminder = async () => {
    setDraftOpen(true);
    if (draft) return;
    setDrafting(true);
    try {
      const r = await api.post("/landlord/announcements/draft", {
        topic: "Friendly reminder: rent is a few days late. Please pay via Stripe or e-Transfer.",
        tone: "friendly",
        importance: "normal",
      });
      setDraft(r.data || null);
    } catch (_) {
      setDraft({
        subject: "Friendly rent reminder",
        body: "Hi there,\n\nThis is a courteous reminder that rent is a few days past due. Please pay when convenient or reach out if you need a moment.\n\nThank you,",
      });
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="landlord-dashboard-v2">
      <div className="flex h-screen overflow-hidden">
        <aside className="w-64 border-r border-border bg-card hidden md:flex flex-col" data-testid="v2-sidebar">
          <div className="p-6 border-b border-border">
            <h1 className="text-xl font-bold tracking-tighter">Central Rentals</h1>
            <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Landlord Copilot</p>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {NAV.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                data-testid={`v2-nav-${item.label.toLowerCase()}`}
                className="flex items-center w-full space-x-3 px-3 py-2 text-sm font-medium rounded-sm hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-6 z-10">
            <div className="flex items-center space-x-4 flex-1 max-w-md">
              <Search className="h-5 w-5 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder={t("v2.search_placeholder", "Search properties, tenants, tickets…")}
                data-testid="v2-search-input"
                className="bg-transparent border-none focus:outline-none text-sm placeholder:text-muted-foreground w-full"
              />
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/briefing" data-testid="v2-briefing-btn" className="p-2 hover:bg-muted rounded-full transition-colors relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-emerald-500 rounded-full" />
              </Link>
              <div className="h-8 w-8 rounded-full bg-muted border border-border" />
            </div>
          </header>

          <div className="flex-1 overflow-y-auto p-6 md:p-10" data-testid="v2-main">
            <div className="max-w-5xl mx-auto space-y-8">
              <div>
                <h2 className="text-3xl font-bold tracking-tight mb-2" data-testid="v2-greeting">{greeting}</h2>
                <p className="text-muted-foreground" data-testid="v2-legal-callout">{legal}</p>
              </div>

              {/* Referral CTA — dismissable per session, links to the
                  existing /referrals reward flow. */}
              <ReferralBanner />

              <NextBestAction
                title={t("v2.nba_title", "Late-rent Autopilot")}
                description={t("v2.nba_desc", "1 tenant is 4 days late. Your Copilot has drafted a friendly reminder.")}
                actionText={t("v2.nba_cta", "Review draft")}
                onAction={openDraftReminder}
                type="warning"
                testid="v2-nba-draft-btn"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="v2-kpi-grid">
                {kpis.map((kpi, i) => (
                  <div
                    key={i}
                    data-testid={`v2-kpi-${i}`}
                    className="p-6 bg-card border border-border rounded-sm shadow-sm hover:border-primary transition-colors"
                  >
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                      {kpi.label}
                    </p>
                    <p className="text-3xl font-bold mt-2 font-mono">{kpi.val}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold tracking-tight">{t("v2.priorities_title", "Today's priorities")}</h3>
                  <Link to="/briefing" className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline" data-testid="v2-briefing-link">
                    {t("v2.priorities_view_all", "View all")} <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
                <ul className="divide-y divide-border border border-border rounded-sm bg-card">
                  {(briefing?.priorities || [
                    { title: "Add your first property to unlock rent collection", route: "/properties", urgency: "high" },
                    { title: "Connect Stripe to accept card + Apple Pay", route: "/billing", urgency: "med" },
                    { title: "Invite a tenant to send them the tenant portal", route: "/leases", urgency: "med" },
                  ]).slice(0, 5).map((p, i) => (
                    <li key={i} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {p.urgency === "high" ? (
                          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                        )}
                        <span className="text-sm font-medium truncate">{p.title}</span>
                      </div>
                      <Link
                        to={p.route || "/dashboard"}
                        data-testid={`v2-priority-${i}`}
                        className="text-xs font-semibold text-primary hover:underline whitespace-nowrap ml-3"
                      >
                        {t("v2.priority_open", "Open")} →
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>

      <InlineSlideOver
        isOpen={draftOpen}
        onClose={() => setDraftOpen(false)}
        title={t("v2.draft_title", "Draft rent reminder")}
      >
        <div className="space-y-4" data-testid="v2-draft-form">
          <p className="text-sm text-muted-foreground">
            {t("v2.draft_hint", "Review the automated draft. Edit anything before it goes out.")}
          </p>
          {drafting ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
              {t("v2.draft_generating", "Drafting…")}
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Subject</span>
                <input
                  data-testid="v2-draft-subject"
                  defaultValue={draft?.subject || ""}
                  className="mt-1 w-full p-2 border border-border rounded-sm bg-muted text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Body</span>
                <textarea
                  data-testid="v2-draft-body"
                  defaultValue={draft?.body || ""}
                  className="mt-1 w-full h-48 p-3 border border-border rounded-sm bg-muted text-sm font-mono"
                />
              </label>
              {draft?.source === "fallback" && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t("v2.draft_fallback", "Backup mode — Copilot credits are topping up. This template is ready to send now.")}
                </p>
              )}
            </>
          )}
          <button
            data-testid="v2-draft-send-btn"
            className="w-full py-2 bg-primary text-primary-foreground hover:opacity-90 rounded-sm text-sm font-medium transition-opacity"
          >
            {t("v2.draft_send", "Send message")}
          </button>
        </div>
      </InlineSlideOver>
    </div>
  );
}
